import { useState, useEffect } from "react";
import { Scale, Clock, Check, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

/**
 * Dialog per verificare una ricetta dopo la cottura.
 * Lo chef inserisce il peso finale reale e opzionalmente il tempo di preparazione.
 */
const VerificaRicettaDialog = ({
  open,
  onOpenChange,
  ricetta,
  onVerifica,
  showCosts = true
}) => {
  const [pesoFinale, setPesoFinale] = useState("");
  const [tempoPreparazione, setTempoPreparazione] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset quando cambia ricetta
  useEffect(() => {
    if (open && ricetta) {
      setPesoFinale(ricetta.peso_finale_kg > 0 ? String(ricetta.peso_finale_kg) : "");
      setTempoPreparazione(ricetta.tempo_preparazione ? String(ricetta.tempo_preparazione) : "");
    }
  }, [open, ricetta]);

  if (!ricetta) return null;

  // Calcola costo totale ingredienti
  const costoTotale = ricetta.ingredienti?.reduce(
    (acc, ing) => acc + (ing.costo_ingrediente || 0), 
    0
  ) || 0;

  // Calcola costo/kg preview
  const pesoNum = parseFloat(pesoFinale) || 0;
  const costoPerKgPreview = pesoNum > 0 ? costoTotale / pesoNum : 0;

  // Calcola peso totale ingredienti (come riferimento)
  const pesoIngredientiGrammi = ricetta.ingredienti?.reduce(
    (acc, ing) => acc + (ing.quantita || 0),
    0
  ) || 0;

  const handleVerifica = async () => {
    if (pesoNum <= 0) return;
    
    setSaving(true);
    try {
      await onVerifica({
        peso_finale_kg: pesoNum,
        tempo_preparazione: tempoPreparazione ? parseInt(tempoPreparazione) : null
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="h-5 w-5" />
            Verifica Ricetta
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Nome ricetta */}
          <div className="bg-muted/50 rounded-xl p-3">
            <p className="font-semibold text-lg">{ricetta.nome}</p>
            <p className="text-sm text-muted-foreground">
              {ricetta.ingredienti?.length || 0} ingredienti{showCosts ? ` • Costo ingredienti: €${costoTotale.toFixed(2)}` : ''}
            </p>
          </div>

          {/* Alert */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-600">Inserisci il peso reale dopo la cottura</p>
              <p className="text-muted-foreground mt-1">
                Peso ingredienti inseriti: {pesoIngredientiGrammi}g ({(pesoIngredientiGrammi/1000).toFixed(2)}kg)
              </p>
            </div>
          </div>

          {/* Peso finale */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Scale className="h-4 w-4" />
              Peso Finale (kg) *
            </Label>
            <Input
              type="number"
              step="0.01"
              placeholder="Es. 0.85"
              value={pesoFinale}
              onChange={(e) => setPesoFinale(e.target.value)}
              className="text-lg"
            />
            <p className="text-xs text-muted-foreground">
              Il peso del prodotto finito dopo la cottura
            </p>
          </div>

          {/* Tempo preparazione */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Tempo Preparazione (minuti)
            </Label>
            <Input
              type="number"
              placeholder="Es. 45"
              value={tempoPreparazione}
              onChange={(e) => setTempoPreparazione(e.target.value)}
            />
          </div>

          {/* Preview costo/kg - solo admin */}
          {showCosts && pesoNum > 0 && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Costo per kg calcolato:</p>
                  <p className="text-2xl font-bold text-green-600">
                    €{costoPerKgPreview.toFixed(2)}/kg
                  </p>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  <p>€{costoTotale.toFixed(2)} ÷ {pesoNum.toFixed(2)}kg</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
          >
            Annulla
          </Button>
          <Button
            onClick={handleVerifica}
            disabled={pesoNum <= 0 || saving}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            <Check className="h-4 w-4 mr-1" />
            {saving ? "Salvataggio..." : "Conferma Verifica"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerificaRicettaDialog;
